import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Play, X } from 'lucide-react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel';

interface MediaItem {
  url: string;
  type: 'image' | 'video';
  caption?: string;
}

const carouselItems: MediaItem[] = [
  { url: 'https://res.cloudinary.com/dkliq9qvu/video/upload/v1766086593/VID_20251017_101719237_rmmp70.mp4', type: 'video', caption: 'iPhone Display Replacement at iFix Apple Care Patna' },
  { url: 'https://res.cloudinary.com/dkliq9qvu/video/upload/v1766084783/VID_20250923_181644171_vpj8sd.mp4', type: 'video', caption: 'iPhone Back Glass Replacement Bihar Service Center' },
  { url: 'https://res.cloudinary.com/dkliq9qvu/image/upload/v1766084262/IMG_20250923_181637314_HDR_zo5hrk.jpg', type: 'image', caption: 'Professional Apple Device Repair Patna Bihar' },
  { url: 'https://res.cloudinary.com/dkliq9qvu/video/upload/v1766084706/VID_20251203_165243731_rq0knn.mp4', type: 'video', caption: 'Expert iPhone Technicians at iFix Apple Care' },
  { url: 'https://res.cloudinary.com/dkliq9qvu/image/upload/v1766084265/IMG_20250520_143928091_yobcah.jpg', type: 'image', caption: 'Genuine Apple Parts Used for Repairs' },
  { url: 'https://res.cloudinary.com/dkliq9qvu/video/upload/v1766084935/VID_20251215_114035075_q03ld6.mp4', type: 'video', caption: 'Precision MacBook Repair Work Patna' },
  { url: 'https://res.cloudinary.com/dkliq9qvu/image/upload/v1766084111/IMG_20251118_150730666_HDR_s2ijkf.jpg', type: 'image', caption: 'Customer Device Repair at Bihar Apple Service Center' },
  { url: 'https://res.cloudinary.com/dkliq9qvu/video/upload/v1766083865/VID_20251206_165554907_twehcl.mp4', type: 'video', caption: 'Final Quality Check - iFix Apple Care Patna' },
];

const MediaCarousel = () => {
  const [selectedMedia, setSelectedMedia] = useState<MediaItem | null>(null);

  const getVideoThumbnail = (url: string) => {
    return url.replace('/video/upload/', '/video/upload/so_0/').replace('.mp4', '.jpg');
  };

  return (
    <section className="section-padding" id="showcase">
      <div className="container-apple">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-semibold text-foreground mb-4">
            Repair Showcase
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Watch our skilled technicians at work. See how we bring your{' '}
            <Link to="/iphone-service" className="text-primary hover:underline">iPhones</Link>,{' '}
            <Link to="/macbook-service" className="text-primary hover:underline">MacBooks</Link>,{' '}
            <Link to="/ipad-service" className="text-primary hover:underline">iPads</Link>, and{' '}
            <Link to="/watch-service" className="text-primary hover:underline">Apple Watches</Link> back to life.
          </p>
        </div>

        <Carousel
          opts={{
            align: 'start',
            loop: true,
          }}
          className="w-full"
        >
          <CarouselContent className="-ml-4">
            {carouselItems.map((item, index) => (
              <CarouselItem key={index} className="pl-4 md:basis-1/2 lg:basis-1/3">
                <div
                  className="relative aspect-[4/3] rounded-2xl overflow-hidden cursor-pointer group glow-card"
                  onClick={() => setSelectedMedia(item)}
                >
                  {item.type === 'video' ? (
                    <>
                      <img
                        src={getVideoThumbnail(item.url)}
                        alt={item.caption ? `${item.caption} - Video` : `Apple repair showcase video ${index + 1} at iFix Apple Care Patna`}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-16 h-16 rounded-full bg-background/90 flex items-center justify-center shadow-xl transition-transform duration-300 group-hover:scale-110">
                          <Play className="w-7 h-7 text-primary ml-1" />
                        </div>
                      </div>
                    </>
                  ) : (
                    <img
                      src={item.url}
                      alt={item.caption ? `${item.caption} - Image` : `Apple repair showcase ${index + 1} at iFix Apple Care Patna Bihar`}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                    />
                  )}
                  
                  {/* Gradient overlay with caption */}
                  <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-foreground/80 via-foreground/20 to-transparent" />
                  {item.caption && (
                    <div className="absolute bottom-4 left-4 right-4">
                      <p className="text-background font-medium text-sm md:text-base">{item.caption}</p>
                    </div>
                  )}
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious className="left-2 lg:-left-12 bg-background/80 backdrop-blur-sm border-border/50 hover:bg-background" />
          <CarouselNext className="right-2 lg:-right-12 bg-background/80 backdrop-blur-sm border-border/50 hover:bg-background" />
        </Carousel>
      </div>

      {/* Lightbox Modal */}
      <Dialog open={!!selectedMedia} onOpenChange={() => setSelectedMedia(null)}>
        <DialogContent className="max-w-4xl p-0 bg-background/95 backdrop-blur-xl border-border/50">
          <button
            onClick={() => setSelectedMedia(null)}
            className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-background/80 flex items-center justify-center hover:bg-background transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
          {selectedMedia?.type === 'video' ? (
            <video
              src={selectedMedia.url}
              controls
              autoPlay
              className="w-full h-auto max-h-[80vh] rounded-lg"
            />
          ) : (
            <img
              src={selectedMedia?.url}
              alt={selectedMedia?.caption ? `${selectedMedia.caption} - Detail view` : 'Apple device repair showcase at iFix Apple Care Patna Bihar'}
              className="w-full h-auto max-h-[80vh] object-contain rounded-lg"
            />
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default MediaCarousel;
